/**
 * Pure TEA update for writing-goal progress: per-file word counts, the
 * chronicle-aligned buffer baselines, and the session / today totals.
 *
 * Shell: `useProjectGoalsStore.ts` (Pinia). The shell resolves the author,
 * reads the chronicle and the active buffer, then reports facts as Msgs.
 * Listener registration and the debounce timer are Cmds.
 */
import { assertNever } from "../lib/assertNever";
import {
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import {
  sumAuthorChronicleWords,
  type GoalsChronicleEntry,
} from "../lib/writingGoals";

export type WordCounts = Readonly<Record<string, number>>;

export type GoalsModel = {
  /** Per-scene word counts for the Book (manuscript) total. */
  readonly fileWordCounts: WordCounts;
  /**
   * Word count last reflected in the Session/Today chronicle totals for a
   * path. Live `doc` minus this is the not-yet-chronicled delta.
   */
  readonly alignedWordCounts: WordCounts;
  /** Last successfully flushed word count per relative path. */
  readonly lastFlushedWordCounts: WordCounts;
  readonly sessionStartedAtUnix: Maybe<number>;
  readonly chronicleSessionWords: number;
  readonly chronicleDailyWords: number;
  readonly chronicleForDate: Maybe<string>;
  readonly goalsAuthorEmail: Maybe<string>;
  readonly lastChronicleMaxSeq: number;
};

export const initialGoalsModel: GoalsModel = {
  fileWordCounts: {},
  alignedWordCounts: {},
  lastFlushedWordCounts: {},
  sessionStartedAtUnix: nothing,
  chronicleSessionWords: 0,
  chronicleDailyWords: 0,
  chronicleForDate: nothing,
  goalsAuthorEmail: nothing,
  lastChronicleMaxSeq: 0,
};

/** The prose buffer currently open (never a map). */
export type ActiveBuffer = {
  readonly pathKey: string;
  readonly docWords: number;
  readonly dirty: boolean;
};

export type GoalsMsg =
  | {
      readonly type: "NoteActiveChapterSaved";
      readonly relativePath: string;
      readonly words: number;
      readonly sceneFiles: readonly string[];
    }
  | {
      readonly type: "NoteFileFlushed";
      readonly relativePath: string;
      readonly words: number;
      readonly sceneFiles: readonly string[];
    }
  | {
      /** Merge freshly read counts; drop files no longer in the manuscript. */
      readonly type: "WordCountsLoaded";
      readonly files: readonly string[];
      readonly counts: WordCounts;
    }
  | {
      readonly type: "EnsureProgress";
      readonly resetSession: boolean;
      readonly nowUnix: number;
      readonly active: Maybe<ActiveBuffer>;
    }
  | {
      readonly type: "AuthorResolved";
      readonly author: Maybe<string>;
      readonly nowUnix: number;
    }
  | {
      readonly type: "ChronicleLoaded";
      readonly author: Maybe<string>;
      readonly entries: readonly GoalsChronicleEntry[];
      readonly maxSeq: number;
      readonly today: string;
      readonly active: Maybe<ActiveBuffer>;
    }
  | { readonly type: "ResetWordCounts" }
  | { readonly type: "ResetSessionBaseline" };

export type GoalsCmd =
  | { readonly kind: "ensureCommitListener" }
  | { readonly kind: "refreshChronicle" }
  | { readonly kind: "clearRefreshTimer" };

function withCount(counts: WordCounts, key: string, words: number): WordCounts {
  return { ...counts, [key]: words };
}

function withSceneCount(
  model: GoalsModel,
  relativePath: string,
  words: number,
  sceneFiles: readonly string[],
): WordCounts {
  return sceneFiles.includes(relativePath)
    ? withCount(model.fileWordCounts, relativePath, words)
    : model.fileWordCounts;
}

export function updateGoals(
  model: GoalsModel,
  msg: GoalsMsg,
): readonly [GoalsModel, readonly GoalsCmd[]] {
  switch (msg.type) {
    case "NoteActiveChapterSaved":
      return [
        {
          ...model,
          alignedWordCounts: withCount(
            model.alignedWordCounts,
            msg.relativePath,
            msg.words,
          ),
          lastFlushedWordCounts: withCount(
            model.lastFlushedWordCounts,
            msg.relativePath,
            msg.words,
          ),
          fileWordCounts: withSceneCount(
            model,
            msg.relativePath,
            msg.words,
            msg.sceneFiles,
          ),
        },
        [],
      ];
    case "NoteFileFlushed":
      return [
        {
          ...model,
          lastFlushedWordCounts: withCount(
            model.lastFlushedWordCounts,
            msg.relativePath,
            msg.words,
          ),
          fileWordCounts: withSceneCount(
            model,
            msg.relativePath,
            msg.words,
            msg.sceneFiles,
          ),
        },
        [],
      ];
    case "WordCountsLoaded": {
      const merged: Record<string, number> = {
        ...model.fileWordCounts,
        ...msg.counts,
      };
      for (const key of Object.keys(merged)) {
        if (msg.files.includes(key) === false) delete merged[key];
      }
      return [{ ...model, fileWordCounts: merged }, []];
    }
    case "EnsureProgress": {
      const startSession =
        msg.resetSession || isNothing(model.sessionStartedAtUnix);
      let next: GoalsModel = startSession
        ? {
            ...model,
            sessionStartedAtUnix: just(msg.nowUnix),
            chronicleSessionWords: msg.resetSession
              ? 0
              : model.chronicleSessionWords,
          }
        : model;
      // Seed alignment for the active buffer so typing credits the delta
      // immediately without waiting for a flush.
      if (
        isJust(msg.active) &&
        isNothing(toMaybe(next.alignedWordCounts[msg.active.value.pathKey]))
      ) {
        const { pathKey, docWords } = msg.active.value;
        next = {
          ...next,
          alignedWordCounts: withCount(
            next.alignedWordCounts,
            pathKey,
            docWords,
          ),
          lastFlushedWordCounts: withCount(
            next.lastFlushedWordCounts,
            pathKey,
            docWords,
          ),
        };
      }
      return [
        next,
        [{ kind: "ensureCommitListener" }, { kind: "refreshChronicle" }],
      ];
    }
    case "AuthorResolved":
      return [
        {
          ...model,
          goalsAuthorEmail: msg.author,
          sessionStartedAtUnix: isNothing(model.sessionStartedAtUnix)
            ? just(msg.nowUnix)
            : model.sessionStartedAtUnix,
        },
        [],
      ];
    case "ChronicleLoaded": {
      const summed = sumAuthorChronicleWords(msg.entries, msg.author, {
        sessionStartedAtUnix: model.sessionStartedAtUnix,
        todayLocal: msg.today,
      });
      // Only fold flushed words into alignment when chronicle actually grew
      // (a new entry landed). A speculative refresh between autosave and
      // commit must not zero bufferDelta early.
      const chronicleAdvanced = msg.maxSeq > model.lastChronicleMaxSeq;
      let aligned = model.alignedWordCounts;
      if (chronicleAdvanced && isJust(msg.active)) {
        const { pathKey, docWords, dirty } = msg.active.value;
        const flushed = toMaybe(model.lastFlushedWordCounts[pathKey]);
        if (isJust(flushed)) {
          aligned = withCount(aligned, pathKey, flushed.value);
        } else if (dirty === false) {
          aligned = withCount(aligned, pathKey, docWords);
        }
      }
      return [
        {
          ...model,
          chronicleSessionWords: summed.session,
          chronicleDailyWords: summed.daily,
          chronicleForDate: just(msg.today),
          alignedWordCounts: aligned,
          lastChronicleMaxSeq: Math.max(model.lastChronicleMaxSeq, msg.maxSeq),
        },
        [],
      ];
    }
    case "ResetWordCounts":
      return [initialGoalsModel, [{ kind: "clearRefreshTimer" }]];
    case "ResetSessionBaseline":
      return [
        {
          ...model,
          sessionStartedAtUnix: nothing,
          chronicleSessionWords: 0,
          chronicleDailyWords: 0,
          chronicleForDate: nothing,
          alignedWordCounts: {},
          lastFlushedWordCounts: {},
          lastChronicleMaxSeq: 0,
        },
        [],
      ];
    default:
      return assertNever(msg);
  }
}
